/**
 * Prévia da IA flutuando sobre a lista, SEM abrir a conversa (abrir não marca
 * nada como lido de qualquer jeito, mas aqui nem as mensagens são lidas).
 *
 * No computador é um balão ao lado da lista, na altura da linha; no celular,
 * uma folha que sobe do rodapé. Sem prévia ainda (ou com mensagens depois
 * dela), dá para pedir ali mesmo: gasta 1 dos pedidos do dia ao Gemini.
 * Fecha com ESC (o foco volta ao botão), clicando fora ou rolando a lista.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiMessageCircle, FiX } from "react-icons/fi";
import styled, { css, keyframes } from "styled-components";

import useMediaQuery from "../../../hooks/useMediaQuery";
import IconeDaIA from "../IconeDaIA";
import { PendenteParaProjetos } from "./AlertaProjetos";
import { ConteudoDaPrevia, PreviaAusente, RodapeDaPrevia } from "./CartaoDaPrevia";
import { BotaoCompacto, BotaoIcone, CELULAR } from "./estilos";
import usePrevia from "./usePrevia";

const LARGURA = 420;
const MARGEM = 12;
const ESPACO_DA_LISTA = 10;

const surgir = keyframes`
  from { opacity: 0; transform: translateX(-4px) scale(0.98); }
  to { opacity: 1; transform: none; }
`;

const subir = keyframes`
  from { opacity: 0.4; transform: translateY(24px); }
  to { opacity: 1; transform: none; }
`;

const Fundo = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1090;
  background: rgba(10, 12, 18, 0.42);
`;

const Balao = styled.div`
  position: fixed;
  z-index: 1100;
  display: flex;
  flex-direction: column;
  background: var(--surface);
  color: var(--ink);
  outline: none;

  ${({ $folha }) =>
    $folha
      ? css`
          left: 0;
          right: 0;
          bottom: 0;
          max-height: 86dvh;
          border-radius: 18px 18px 0 0;
          box-shadow: var(--shadow-4);
          padding-bottom: var(--safe-area-bottom, 0px);
          animation: ${subir} 220ms cubic-bezier(0.23, 1, 0.32, 1);
        `
      : css`
          width: ${LARGURA}px;
          max-width: calc(100vw - ${MARGEM * 2}px);
          max-height: calc(100dvh - ${MARGEM * 2}px);
          border: 1px solid var(--border);
          border-radius: 14px;
          box-shadow: var(--shadow-3);
          transform-origin: left top;
          animation: ${surgir} 160ms cubic-bezier(0.23, 1, 0.32, 1);
        `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Alca = styled.span`
  align-self: center;
  width: 36px;
  height: 4px;
  margin-top: 8px;
  border-radius: 2px;
  background: var(--border-strong);
`;

const Cabeca = styled.header`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 10px 8px 4px 18px;

  > span {
    display: grid;
    place-items: center;
    flex: 0 0 auto;
    width: 26px;
    height: 26px;
    border-radius: 8px;
    background: var(--surface-2);
    color: var(--ink-2);
  }

  > div {
    flex: 1 1 auto;
    min-width: 0;
  }

  small {
    display: block;
    font-size: 12px;
    color: var(--ink-3);
  }

  h2 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--ink);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Miolo = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 12px 18px 16px;
`;

const Pe = styled.footer`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 10px 12px 12px 18px;
  border-top: 1px solid var(--divider);
`;

const RodapeDoMiolo = styled.div`
  margin-top: 16px;
`;

const TopoDoMiolo = styled.div`
  margin-bottom: 18px;

  &:empty {
    display: none;
  }
`;

/**
 * Ao lado da coluna da lista, na altura da linha; sobe o que for preciso para
 * caber. O pé da coluna é o limite de baixo, não o da janela: com a janela
 * maior que a área visível (passando por baixo da barra de tarefas), o pé da
 * prévia sumia (30/10). Acima disso, a prévia rola por dentro.
 */
function calcularPosicao(gatilho, altura) {
  const linha = gatilho.closest("li") || gatilho;
  const coluna = gatilho.closest("[data-coluna-lista]") || linha;
  const caixaDaLinha = linha.getBoundingClientRect();
  const caixaDaColuna = coluna.getBoundingClientRect();
  const largura = Math.min(LARGURA, window.innerWidth - MARGEM * 2);
  const left = Math.min(caixaDaColuna.right + ESPACO_DA_LISTA, window.innerWidth - largura - MARGEM);
  const fundo = Math.min(window.innerHeight - MARGEM, caixaDaColuna.bottom);
  const top = Math.max(MARGEM, Math.min(caixaDaLinha.top - 6, fundo - altura));
  return { left, top, maxHeight: Math.max(0, fundo - top) };
}

export default function PreviaFlutuante({ conversa, gatilho, aoFechar, aoAbrirConversa, aoAtualizarItem }) {
  const folha = useMediaQuery(CELULAR);
  const caixa = useRef(null);
  const [posicao, setPosicao] = useState(null);
  const previa = usePrevia(conversa, aoAtualizarItem);

  const fechar = useCallback(
    (devolverFoco) => {
      aoFechar();
      if (devolverFoco) gatilho.current?.focus?.();
    },
    [aoFechar, gatilho],
  );

  useLayoutEffect(() => {
    if (folha || !gatilho.current) return;
    setPosicao(calcularPosicao(gatilho.current, caixa.current?.offsetHeight || 0));
  }, [folha, gatilho, conversa.previa, previa.estado.carregando]);

  useEffect(() => {
    caixa.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    const aoTeclar = (evento) => {
      if (evento.key !== "Escape") return;
      // Marcado: o mesmo ESC não fecha também a conversa aberta ao lado.
      evento.preventDefault();
      fechar(true);
    };
    const aoApertarFora = (evento) => {
      const alvo = evento.target;
      if (caixa.current?.contains(alvo) || gatilho.current?.contains(alvo)) return;
      fechar(false);
    };
    const aoRolar = (evento) => {
      if (!caixa.current?.contains(evento.target)) fechar(false);
    };
    document.addEventListener("keydown", aoTeclar);
    document.addEventListener("pointerdown", aoApertarFora, true);
    if (!folha) {
      window.addEventListener("scroll", aoRolar, true);
      window.addEventListener("resize", aoFechar);
    }
    return () => {
      document.removeEventListener("keydown", aoTeclar);
      document.removeEventListener("pointerdown", aoApertarFora, true);
      window.removeEventListener("scroll", aoRolar, true);
      window.removeEventListener("resize", aoFechar);
    };
  }, [fechar, aoFechar, gatilho, folha]);

  const estilo = folha
    ? undefined
    : { left: posicao?.left ?? -9999, top: posicao?.top ?? 0, maxHeight: posicao?.maxHeight };
  return createPortal(
    <>
      {folha ? <Fundo aria-hidden="true" /> : null}
      <Balao
        ref={caixa}
        role="dialog"
        aria-modal={folha ? "true" : undefined}
        aria-label={`Prévia da IA: ${conversa.nome}`}
        tabIndex={-1}
        $folha={folha}
        style={estilo}
      >
        {folha ? <Alca aria-hidden="true" /> : null}
        <Cabeca>
          <span aria-hidden="true"><IconeDaIA tamanho={15} /></span>
          <div>
            <small>Prévia da IA</small>
            <h2 title={conversa.nome}>{conversa.nome}</h2>
          </div>
          <BotaoIcone type="button" aria-label="Fechar a prévia" title="Fechar" onClick={() => fechar(true)}>
            <FiX size={18} aria-hidden="true" />
          </BotaoIcone>
        </Cabeca>
        <Miolo>
          <TopoDoMiolo>
            <PendenteParaProjetos conversa={conversa} />
          </TopoDoMiolo>
          {conversa.previa ? (
            <>
              <ConteudoDaPrevia previa={conversa.previa} />
              <RodapeDoMiolo>
                <RodapeDaPrevia previa={conversa.previa} estado={previa.estado} gemini={previa.gemini} aoAtualizar={previa.gerar} />
              </RodapeDoMiolo>
            </>
          ) : (
            <PreviaAusente conversa={conversa} estado={previa.estado} gemini={previa.gemini} aoAtualizar={previa.gerar} />
          )}
        </Miolo>
        <Pe>
          <BotaoCompacto type="button" onClick={() => aoAbrirConversa(conversa)}>
            <FiMessageCircle size={15} aria-hidden="true" />
            Abrir conversa
          </BotaoCompacto>
        </Pe>
      </Balao>
    </>,
    document.body,
  );
}
